// Animate only the final live renderer. No old bitmap, crossfade, or delayed swap.
export function animateChartLayout({ chart, plot, update, driver, onFinish }) {
  const before = plot?.getBoundingClientRect();
  if (!chart || !before?.width || !before.height || !driver?.flushLayout ||
      typeof Element.prototype.animate !== 'function' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    update(); driver?.resize(); onFinish(); return null;
  }
  const movedCanvases = [];
  const sidebars = [...document.querySelectorAll('.left-nav, .right-nav')].map(node => ({
    node, rect: node.getBoundingClientRect(), copy: node.cloneNode(true), opacity: node.style.getPropertyValue('opacity'),
    priority: node.style.getPropertyPriority('opacity'),
  }));
  const sidebarLayers = [];
  const host = chart.parentElement;
  const hostClipping = ['overflow', 'clip-path'].map(key => [key, host.style.getPropertyValue(key), host.style.getPropertyPriority(key)]);
  let hostRect = host.getBoundingClientRect();
  const hostScale = (host.offsetWidth ? hostRect.width / host.offsetWidth : 1) || 1;
  const divider = host.querySelector('.resize-history-position');
  const oldDivider = divider?.getBoundingClientRect();
  const companions = [divider, host.querySelector('.bottom-section'), host.querySelector('#chartcontrols')].filter(Boolean).map(node => ({
    node, rect: node.getBoundingClientRect(), css: node.style.cssText,
    display: getComputedStyle(node).display,
  }));
  const companionAnimations = [];
  let layer, movement, startFrame, finished = false;
  const visibility = [plot.style.getPropertyValue('visibility'), plot.style.getPropertyPriority('visibility')];
  const finish = () => {
    if (finished) return;
    finished = true;
    cancelAnimationFrame(startFrame);
    // Restore the same live canvases, in their original order, within one task.
    movedCanvases.reverse().forEach(({ canvas, parent, next, css }) => {
      parent.insertBefore(canvas, next?.parentNode === parent ? next : null);
      canvas.style.cssText = css;
    });
    movement?.cancel();
    companionAnimations.forEach(animation => animation.cancel());
    companions.forEach(({ node, css }) => { node.style.cssText = css; });
    sidebarLayers.forEach(node => node.remove());
    sidebars.forEach(({ node, opacity, priority }) => {
      if (opacity) node.style.setProperty('opacity', opacity, priority);
      else node.style.removeProperty('opacity');
    });
    layer?.remove();
    hostClipping.forEach(([key, value, priority]) => {
      if (value) host.style.setProperty(key, value, priority);
      else host.style.removeProperty(key);
    });
    if (visibility[0]) plot.style.setProperty('visibility', ...visibility);
    else plot.style.removeProperty('visibility');
    chart.dataset.panelAnimating = 'false';
    driver.restore?.();
    onFinish();
  };
  try {
    driver.prepare?.();
    update();
    chart.dataset.panelAnimating = 'true';
    // Settle the final geometry before the browser paints any intermediate size.
    driver.resize();
    driver.flushLayout();
    const after = plot.getBoundingClientRect();
    // Fullscreen also moves the host when the sidebars collapse.
    // All endpoint coordinates must use that new containing block.
    hostRect = host.getBoundingClientRect();
    // The restore endpoint is narrower than the moving surface. Keep its
    // horizontal extent visible until motion finishes, while still clipping
    // the table vertically above the account footer.
    host.style.setProperty('overflow', 'visible', 'important');
    host.style.setProperty('clip-path', `inset(0 -${Math.max(before.width, after.width) / hostScale}px)`, 'important');
    // Read the endpoint before starting ANY animations: fill:'both' immediately
    // transforms the divider, so reading it inside the loop yields its start.
    const finalDivider = divider?.getBoundingClientRect();
    // Move the live divider/table on the same compositor timeline as the chart.
    // Layout and canvas redraw happen once, never on every animation frame.
    const timing = { duration: 320, easing: 'cubic-bezier(.4, 0, .2, 1)', fill: 'both' };
    const bodyScale = document.body.getBoundingClientRect().width / document.body.offsetWidth || 1;
    sidebars.forEach(({ node, rect, copy }) => {
      const destination = node.getBoundingClientRect();
      if (Math.abs(rect.width - destination.width) < 1) return;
      const closing = rect.width > destination.width;
      const expanded = closing ? rect : destination;
      const visual = closing ? copy : node.cloneNode(true);
      visual.removeAttribute('id');
      visual.querySelectorAll('[id]').forEach(child => child.removeAttribute('id'));
      [visual, ...visual.querySelectorAll('*')].forEach(child => {
        child.style.setProperty('animation', 'none', 'important');
        child.style.setProperty('transition', 'none', 'important');
      });
      visual.inert = true;
      visual.setAttribute('aria-hidden', 'true');
      visual.dataset.chartSidebarMotion = 'true';
      const styles = { position: 'fixed', left: `${expanded.left / bodyScale}px`,
        top: `${expanded.top / bodyScale}px`, width: `${expanded.width / bodyScale}px`,
        height: `${expanded.height / bodyScale}px`, 'min-width': '0', 'max-width': 'none',
        margin: '0', opacity: '1', visibility: 'visible', overflow: 'hidden',
        'pointer-events': 'none', 'z-index': '50' };
      Object.entries(styles).forEach(([key, value]) => visual.style.setProperty(key, value, 'important'));
      node.parentElement.appendChild(visual);
      sidebarLayers.push(visual);
      node.style.setProperty('opacity', '0', 'important');
      const direction = node.classList.contains('left-nav') ? -1 : 1;
      const shifted = `translateX(${direction * Math.abs(rect.width - destination.width) / bodyScale}px)`;
      const animation = visual.animate(closing ? [{ transform: 'none' }, { transform: shifted }]
        : [{ transform: shifted }, { transform: 'none' }], timing);
      animation.pause(); animation.currentTime = 0;
      companionAnimations.push(animation);
    });
    companions.forEach(({ node, rect, display }) => {
      let destination = node.getBoundingClientRect();
      if (node.classList.contains('bottom-section')) {
        const endTop = finalDivider?.bottom ?? after.bottom;
        const startTop = rect.height ? rect.top : (oldDivider?.bottom ?? before.bottom);
        // Final flex sizing may be zero (hidden) or shorter than the old table.
        // Keep one full-height live table surface throughout the motion. These
        // must override the theme's position/height/flex !important rules.
        const styles = {
          position: 'absolute', left: '0px',
          top: `${(endTop - hostRect.top) / hostScale}px`, bottom: 'auto', right: 'auto',
          width: `${hostRect.width / hostScale}px`,
          height: `${Math.max(rect.height, destination.height, hostRect.bottom - Math.min(startTop, endTop)) / hostScale}px`,
          'min-height': '0', 'max-height': 'none', flex: 'none', margin: '0',
          display: display === 'none' ? 'flex' : display, transition: 'none', 'pointer-events': 'none',
        };
        Object.entries(styles).forEach(([key, value]) => node.style.setProperty(key, value, 'important'));
        destination = node.getBoundingClientRect();
      }
      const startTop = rect.height ? rect.top : (oldDivider?.bottom ?? before.bottom);
      // Keep the table and controls above the temporary chart surface. A
      // viewport-level canvas layer otherwise covers them until it is removed.
      if (getComputedStyle(node).position === 'static') node.style.position = 'relative';
      node.style.setProperty('z-index', '2', 'important');
      // A hidden account panel has a zero DOM rect. Restore it from the
      // fullscreen chart's width/left, not its already-narrow endpoint.
      const startRect = rect.width ? rect : before;
      const animation = node.animate([
        { transformOrigin: '0 0', transform: `translate(${(startRect.left - destination.left) / hostScale}px, ${(startTop - destination.top) / hostScale}px) scaleX(${destination.width ? startRect.width / destination.width : 1})` },
        { transformOrigin: '0 0', transform: 'none' },
      ], timing);
      animation.pause();
      animation.currentTime = 0;
      companionAnimations.push(animation);
    });
    layer = document.createElement('div');
    layer.dataset.chartMotionOverlay = 'true';
    layer.setAttribute('aria-hidden', 'true');
    Object.assign(layer.style, { position: 'absolute', pointerEvents: 'none', overflow: 'hidden', zIndex: '1',
      transformOrigin: '0 0', willChange: 'transform', background: getComputedStyle(plot).backgroundColor,
      left: `${(after.left-hostRect.left)/(hostScale || 1)}px`, top: `${(after.top-hostRect.top)/(hostScale || 1)}px`, width: `${after.width/(hostScale || 1)}px`, height: `${after.height/(hostScale || 1)}px` });
    for (const canvas of plot.querySelectorAll('canvas')) {
      const rect = canvas.getBoundingClientRect();
      if (!canvas.width || !canvas.height || !rect.width || !rect.height) continue;
      movedCanvases.push({ canvas, parent: canvas.parentNode, next: canvas.nextSibling, css: canvas.style.cssText });
      Object.assign(canvas.style, { position: 'absolute', left: `${(rect.left-after.left)/after.width*100}%`,
        top: `${(rect.top-after.top)/after.height*100}%`, width: `${rect.width/after.width*100}%`, height: `${rect.height/after.height*100}%` });
      layer.appendChild(canvas);
    }
    if (!movedCanvases.length) { finish(); return null; }
    host.appendChild(layer);
    plot.style.setProperty('visibility', 'hidden', 'important');
    movement = layer.animate([
      { transform: `translate(${(before.left-after.left)/(hostScale || 1)}px, ${(before.top-after.top)/(hostScale || 1)}px) scale(${before.width/after.width}, ${before.height/after.height})` },
      { transform: 'none' },
    ], timing);
    movement.pause();
    movement.currentTime = 0;
    // The timeline still refers to the frame BEFORE synchronous chart layout.
    // Starting there spends the animation duration on layout on busy devices.
    // Paint the start pose first; the following frame has a fresh timestamp.
    // One rAF alone can still belong to the frame blocked by chart layout.
    startFrame = requestAnimationFrame(() => {
      startFrame = requestAnimationFrame(startTime => {
        [movement, ...companionAnimations].forEach(animation => {
          animation.play();
          animation.startTime = startTime;
        });
      });
    });
    Promise.all([movement, ...companionAnimations].map(animation => animation.finished)).then(finish, finish);
  } catch (error) { finish(); throw error; }
  return { cancel: finish };
}
